import { Button } from "components/DesignSystem/Button/Button";
import { BareInput } from "components/DesignSystem/Input/Input";
import { IncorporationFormSource } from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { QuestionOption } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { useMemo } from "react";
import { TrashSimple } from "components/icons/TrashSimple";
import { useUpdateFormTreeNodeMutation } from "./useFormTreeNodeMutations";

const MIN_OPTIONS_REQUIRED = 2;
const NEW_OPTION_DEFAULT_LABEL = "New option";

export const IncorporationQuestionOptionsList = ({
  questionId,
  options,
  source,
  treeId,
  isDisabled = false,
}: {
  questionId: string;
  options: QuestionOption[];
  source: IncorporationFormSource;
  treeId: string;
  isDisabled?: boolean;
}) => {
  const { alertToast } = useToast();
  const [updateQuestionOption, { isLoading: isAddingOption }] =
    useUpdateFormTreeNodeMutation({ source });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const onAddOption = async () => {
    try {
      await updateQuestionOption({
        treeId,
        body: {
          question: {
            node_id: questionId,
            option: {
              action: "add",
              label: NEW_OPTION_DEFAULT_LABEL,
              value: NEW_OPTION_DEFAULT_LABEL,
            },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const onUpdateOptionLabel = async (optionId: string, label: string) => {
    try {
      await updateQuestionOption({
        treeId,
        body: {
          question: {
            node_id: questionId,
            option: {
              action: "update",
              option_id: optionId,
              label,
              value: label,
            },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const onDeleteOption = async (optionId: string) => {
    try {
      await updateQuestionOption({
        treeId,
        body: {
          question: {
            node_id: questionId,
            option: { action: "delete", option_id: optionId },
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const canDeleteOption = options.length > MIN_OPTIONS_REQUIRED;

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {options.map((option, index) => (
        <IncorporationQuestionOptionRow
          key={option.uuid}
          option={option}
          order={index + 1}
          canDelete={canDeleteOption}
          isDisabled={isDisabled}
          onLabelChange={(label) => onUpdateOptionLabel(option.uuid, label)}
          onDelete={() => onDeleteOption(option.uuid)}
        />
      ))}
      <Button
        type="button"
        customType="secondary"
        size="small"
        isLoading={isAddingOption}
        disabled={isAddingOption || isDisabled}
        onClick={onAddOption}
        className="t-self-start"
      >
        + Add option
      </Button>
    </div>
  );
};

const IncorporationQuestionOptionRow = ({
  option,
  order,
  canDelete,
  isDisabled,
  onLabelChange,
  onDelete,
}: {
  option: QuestionOption;
  order: number;
  canDelete: boolean;
  isDisabled: boolean;
  onLabelChange: (label: string) => void;
  onDelete: () => void;
}) => {
  const debouncedSaveLabel = useMemo(
    () => debounce(onLabelChange),
    [option.uuid]
  );

  return (
    <div className="t-flex t-items-end t-gap-2">
      <div className="t-flex-1">
        <BareInput
          label={`Option ${order}`}
          required
          defaultValue={option.label}
          placeholder="Enter option"
          disabled={isDisabled}
          customSize="small"
          onChange={(e) => debouncedSaveLabel(e.target.value)}
        />
      </div>
      <Button
        type="button"
        customType="icon"
        size="small"
        disabled={!canDelete || isDisabled}
        onClick={onDelete}
      >
        <TrashSimple />
      </Button>
    </div>
  );
};
